import {
  type ChangeConsequences,
  type ChangingKind,
  CANCEL_CHANGE,
  CONFIRM_CHANGE,
  changeConsequences,
  changeConsequencesSchema,
  consequencesText,
} from '@sigora/domain';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { type ArtifactStateRow, type Study, fetchArtifactState } from '../api/client.ts';
import { isApiError } from '../api/errors.ts';

/**
 * What a change refused as `change_unacknowledged` would do below it, as the server judged it, or
 * `null` for any other failure. A detail that does not parse is `null` too, so the step reports it
 * as the failure it is rather than asking the researcher to confirm something it cannot describe.
 */
export const unacknowledgedChangeOf = (failure: unknown): ChangeConsequences | null => {
  if (!isApiError(failure) || failure.code !== 'change_unacknowledged') {
    return null;
  }

  const parsed = changeConsequencesSchema.safeParse(failure.detail.consequences);

  return parsed.success ? parsed.data : null;
};

/**
 * What a change on a step would do below it, worked out from the study as it stands: the step's own
 * notice reads it before anything is changed. The server judges a change again under the study row
 * lock when it is made, so this only says what a change would affect; the confirm a change waits in
 * lists what the server found. `null` for a step with nothing it can change, and until the study's
 * artifacts have been read.
 */
export const useStepConsequences = ({
  studyId,
  study,
  changing,
  refreshToken,
}: {
  studyId: string;
  study: Study | null;
  changing: ChangingKind | null;
  refreshToken: number;
}): ChangeConsequences | null => {
  const [artifacts, setArtifacts] = useState<{
    studyId: string;
    rows: readonly ArtifactStateRow[];
  } | null>(null);

  useEffect(() => {
    if (changing === null) {
      return;
    }

    let current = true;
    fetchArtifactState(studyId).then(
      (rows) => {
        if (current) {
          setArtifacts({ studyId, rows });
        }
      },
      // A notice that cannot be worked out is left out: the change is still judged when it is made.
      () => {
        if (current) {
          setArtifacts(null);
        }
      },
    );

    return () => {
      current = false;
    };
  }, [studyId, changing, refreshToken]);

  if (changing === null || study === null || artifacts?.studyId !== studyId) {
    return null;
  }

  return changeConsequences({
    changing,
    artifacts: artifacts.rows,
    materialsConfirmed: study.steps.some(
      (step) => step.slug === 'materials' && step.status === 'complete',
    ),
    readyNow: study.state === 'ready',
  });
};

/**
 * The step's notice of what a change there would affect below it, shown above its controls while
 * something below it is held; nothing otherwise.
 */
export const ChangeNotice = ({ consequences }: { consequences: ChangeConsequences | null }) => {
  const text = consequences === null ? '' : consequencesText(consequences);
  if (text === '') {
    return null;
  }

  return (
    <p className="change-notice" data-test-id="change-notice">
      {text}
    </p>
  );
};

/**
 * The confirm a change waits in once the server has said what it would do below it: the
 * consequences as the server judged them, "Make the change", which sends it again acknowledged, and
 * "Keep it as it is", which sends nothing and leaves whatever was typed as it is. Escape keeps it as
 * it is. The first button takes the
 * focus, so a keyboard user lands on the decision the change is waiting for.
 */
export const ChangeConfirm = ({
  consequences,
  busy,
  onConfirm,
  onCancel,
}: {
  consequences: ChangeConsequences;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const confirmButton = useRef<HTMLButtonElement>(null);

  // Taken while the confirm is committed, so a cancel that hands the focus back to an editor
  // cannot be overtaken by this focus landing after it.
  useLayoutEffect(() => {
    confirmButton.current?.focus();
  }, []);

  return (
    <div
      aria-label="Confirm this change"
      className="change-confirm"
      data-test-id="change-confirm"
      role="group"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !busy) {
          event.preventDefault();
          // Kept from an editor around it, whose Escape would close what the reader typed.
          event.stopPropagation();
          onCancel();
        }
      }}
    >
      <p data-test-id="change-confirm-text">{consequencesText(consequences)}</p>
      <div className="change-confirm-actions">
        <button
          type="button"
          data-test-id="change-confirm-yes"
          ref={confirmButton}
          disabled={busy}
          onClick={onConfirm}
        >
          {CONFIRM_CHANGE}
        </button>
        <button type="button" data-test-id="change-confirm-no" disabled={busy} onClick={onCancel}>
          {CANCEL_CHANGE}
        </button>
      </div>
    </div>
  );
};
